import React, { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Download, Upload } from 'lucide-react';
import { usePurchaseOrderCsvParser } from '../../../hooks/usePurchaseOrderCsvParser';
import { usePurchaseOrderCsvUpload } from '../usePurchaseOrderCsvUpload';

const TEMPLATE_CSV = `SKU,Order_Quantity
TSHIRT-BLK-L,25
HOODIE-RED-M,15`;

const UploadStep: React.FC = () => {
  const { setFile, setParsedRows, setStep } = usePurchaseOrderCsvUpload();

  const {
    file,
    parsedData,
    parseErrors,
    isParsing,
    fileInputRef,
    handleFileChange: baseHandleFileChange,
    resetParser,
    downloadTemplate,
    triggerFileSelect,
  } = usePurchaseOrderCsvParser();

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      // Let the parser do its thing first
      await baseHandleFileChange(e);
    },
    [baseHandleFileChange]
  );

  // When parsedData arrives with no errors, advance to validation
  const hasAdvanced = React.useRef(false);
  React.useEffect(() => {
    if (hasAdvanced.current) return;
    if (parsedData.length > 0 && parseErrors.length === 0 && file) {
      hasAdvanced.current = true;
      setFile(file);
      setParsedRows(parsedData);
      setStep('validation');
    }
  }, [parsedData, parseErrors, file, setFile, setParsedRows, setStep]);

  const handleDownloadTemplate = () => {
    downloadTemplate(TEMPLATE_CSV, 'purchase-order-template.csv');
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const droppedFile = e.dataTransfer.files[0];
      if (!droppedFile) return;

      // Set files on the input and call the change handler with a
      // synthetic React-compatible event so the parser hook processes
      // the dropped file the same way as a manual file selection.
      const dt = new DataTransfer();
      dt.items.add(droppedFile);

      if (fileInputRef.current) {
        fileInputRef.current.files = dt.files;
        const syntheticEvent = {
          target: fileInputRef.current,
        } as React.ChangeEvent<HTMLInputElement>;
        baseHandleFileChange(syntheticEvent);
      }
    },
    [fileInputRef, baseHandleFileChange]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  return (
    <div className="space-y-4">
      {/* Info Alert */}
      <Alert className="border-blue-200 bg-blue-50">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Upload a CSV of SKUs and order quantities. SKUs not currently stocked
          at this location will be auto-activated when the order is saved.
          Quantities in the CSV will overwrite matching SKUs in this order.
        </AlertDescription>
      </Alert>

      {/* Parse Errors */}
      {parseErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-medium mb-1">CSV Errors:</div>
            <ul className="list-disc list-inside text-sm space-y-0.5">
              {parseErrors.slice(0, 5).map((error, i) => (
                <li key={i}>{error}</li>
              ))}
              {parseErrors.length > 5 && (
                <li>
                  ...and {parseErrors.length - 5} more errors
                </li>
              )}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Template Download */}
      <div className="space-y-2">
        <Button
          variant="outline"
          onClick={handleDownloadTemplate}
          className="w-full justify-start"
        >
          <Download className="h-4 w-4 mr-2" />
          Download Template CSV
        </Button>
        <p className="text-xs text-muted-foreground">
          Template includes: SKU, Type, Color, Size, Current_Stock, Order_Quantity (only SKU and Order_Quantity are required)
        </p>
      </div>

      {/* Drag & Drop Zone */}
      <div
        className="border-2 border-dashed rounded-md p-8 text-center hover:border-threadheads-purple/40 transition-colors"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">
            <label
              htmlFor="po-csv-upload"
              className="font-medium text-primary hover:underline cursor-pointer"
            >
              Choose a CSV file
            </label>
            <p>or drag and drop</p>
          </div>
          <input
            ref={fileInputRef}
            id="po-csv-upload"
            type="file"
            className="hidden"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isParsing}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={triggerFileSelect}
            disabled={isParsing}
          >
            {isParsing ? 'Processing...' : 'Select File'}
          </Button>
          {file && parseErrors.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Selected: {file.name}
            </p>
          )}
        </div>
      </div>

      {/* If there are parse errors, allow retry */}
      {parseErrors.length > 0 && (
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={resetParser}>
            Clear &amp; Try Again
          </Button>
        </div>
      )}
    </div>
  );
};

export default UploadStep;
